/**
 * ทางหนีไฟสำหรับ Safari รุ่นเก่า (เป้าหมาย iOS 13.4+ / Safari 13.1)
 * ลูกค้า iPhone รุ่นเก่ายังเข้าเว็บอยู่ — CSS ใหม่ที่เบราว์เซอร์ไม่รู้จักจะถูกทิ้งทั้งบรรทัด
 * แล้วกล่องยุบ/ล้นทับกัน (เจอจริงที่กงล้อ 7 ต.ค. 2026)
 */

/** aspect-ratio มีตั้งแต่ Safari 15 — ก่อนนั้นใช้ padding-top ใน ::before แทน */
export const NO_ASPECT_RATIO = "@supports not (aspect-ratio: 1 / 1)";

/** dvh มีตั้งแต่ Safari 15.4 — ก่อนนั้นใส่ค่า vh ทับไว้ใน block นี้ */
export const NO_DVH = "@supports not (height: 1dvh)";

/** แทน inset ที่ Safari ก่อน 14.5 ไม่รู้จัก */
export function insetSx(
  top: number | string = 0,
  right: number | string = top,
  bottom: number | string = top,
  left: number | string = right
) {
  return { bottom, left, right, top } as const;
}

/** กล่องสัดส่วนคงที่ — ลูกทุกตัวต้องวาง absolute เพราะ ::before กินความสูงทั้งกล่อง */
export function ratioBoxFallbackSx(widthOverHeight: number) {
  return {
    [NO_ASPECT_RATIO]: {
      "&::before": {
        content: '""',
        display: "block",
        paddingTop: `${(100 / widthOverHeight).toFixed(4)}%`,
      },
    },
  };
}

export const NO_FLEX_GAP_CLASS = "no-flexgap";

/**
 * flex gap มีตั้งแต่ Safari 14.1 แต่ @supports แยกไม่ได้ (grid gap มีมานานแล้ว)
 * จึงวัดจริงครั้งเดียวแล้วติดคลาสที่ <html> ให้ CSS ใส่ margin แทน
 */
export function markNoFlexGap() {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (root.dataset.flexGapChecked) return;
  root.dataset.flexGapChecked = "1";
  const probe = document.createElement("div");
  probe.style.cssText =
    "display:flex;flex-direction:column;row-gap:1px;position:absolute;visibility:hidden";
  probe.appendChild(document.createElement("div"));
  probe.appendChild(document.createElement("div"));
  document.body.appendChild(probe);
  const supported = probe.scrollHeight === 1;
  probe.parentNode?.removeChild(probe);
  if (!supported) root.classList.add(NO_FLEX_GAP_CLASS);
}

type Spacing = number | string;

/** selector ลูกทุกตัวยกเว้นตัวท้าย เมื่อไม่มี flex gap — ใช้ตรง ๆ เมื่อทิศทางเปลี่ยนตามจอ */
export const NO_FLEX_GAP_CHILDREN = `html.${NO_FLEX_GAP_CLASS} & > *:not(:last-child)`;

/**
 * sx: ระยะห่างแทน gap ของ flex เมื่อ <html> ติดคลาส no-flexgap
 * ใส่ margin ฝั่งท้ายของลูก (ไม่ใช่ฝั่งหน้า) จะได้ไม่ทับ ml: "auto" ที่ใช้ดันปุ่มชิดขวา
 */
export function flexGapFallbackSx(
  spacing: Spacing | Partial<Record<"xs" | "sm" | "md" | "lg", Spacing>>,
  direction: "row" | "column" = "row"
) {
  return {
    [NO_FLEX_GAP_CHILDREN]:
      direction === "row" ? { mr: spacing } : { mb: spacing },
  };
}

/** flex ที่ตัดบรรทัด (flexWrap) — ห่างทั้งสองแกน แล้วดึงขอบล่างกลับ */
export function flexWrapGapFallbackSx(row: Spacing, column: Spacing = row) {
  return {
    [`html.${NO_FLEX_GAP_CLASS} &`]: { mb: typeof row === "number" ? -row : `calc(-1 * ${row})` },
    [`html.${NO_FLEX_GAP_CLASS} & > *`]: { mb: row, mr: column },
  };
}
